import { z } from 'zod';
import { IsoTimestamp, RunId, UserId } from './common';

/**
 * Run-level Alert queue (#657 — 2026-08-06).
 *
 * Persistent surface for the four `counters.needsAttention` bump sites
 * in `packages/worker-core/src/run-cycle.ts` (login failure, location
 * mismatch, Workday-credentials-missing, external-apply timeout). Sits
 * alongside the per-question `/v1/needs-review` feed; the mobile Inbox
 * renders both. The run-complete push badge + "Needs attention N"
 * clause are recomputed from the union of open alerts + open reviews so
 * the notification is definitionally a promise the Inbox keeps.
 *
 * Deliberately kept as a subset of the Phase-2 `/v1/inbox` Alerts feed
 * (#429/430/432/434). Adding new kinds is a producer + enum change
 * only; the stored shape does not move. Client-side action/deep-link
 * derivation from `kind` is Phase-3 work and does not touch storage.
 *
 * Dedupe identity: one row per `(userId, alertKey)`. Re-emission bumps
 * `occurrences`, refreshes `runId` + `message`, pins `createdAt` to the
 * first emission.
 */

export const AlertKind = z.enum([
  'login_failed',
  'location_mismatch',
  'workday_credentials_missing',
  'external_apply_timeout',
]);
export type AlertKind = z.infer<typeof AlertKind>;

const ALERT_STATUS_VALUES = ['open', 'dismissed'] as const;
export const AlertStatus = z.enum(ALERT_STATUS_VALUES);
export type AlertStatus = z.infer<typeof AlertStatus>;

/** Forward-compat read variant — see #482. */
export const AlertStatusReadable = z
  .enum([...ALERT_STATUS_VALUES, 'unknown'] as const)
  .catch('unknown');
export type AlertStatusReadable = z.infer<typeof AlertStatusReadable>;

/**
 * URL-safe by construction so `alertKey` can double as the PATCH route
 * segment. Matches `login_failed` (singleton per user) and the
 * `<kind>__<scope>` forms (e.g. `location_mismatch__4434161429`,
 * `workday_credentials_missing__acme.wd5.myworkdayjobs.com`).
 */
export const AlertKey = z.string().regex(/^[a-z0-9._-]+(__[a-z0-9._-]+)?$/);

/**
 * NOT `.strict()` on purpose. The mobile client zod-parses list
 * responses; any future server field or new kind must not brick old
 * app builds. Producer input is enum-validated at the POST boundary
 * via `CreateAlertRequest` (which IS strict).
 */
export const AlertItem = z.object({
  userId: UserId,
  alertKey: AlertKey,
  /** Open string in storage so older clients survive new kinds. */
  kind: z.string().min(1),
  runId: RunId,
  jobTitle: z.string().min(1).nullable(),
  company: z.string().min(1).nullable(),
  jobUrl: z.string().url().nullable(),
  atsHost: z.string().nullable(),
  /** Worker-composed human copy. Fallback for unknown kinds on mobile. */
  message: z.string().min(1).max(2000),
  /**
   * The raw ATS-form residency-gate question that triggered a
   * `location_mismatch` alert (#695). Only populated for that kind —
   * `login_failed` / `workday_credentials_missing` /
   * `external_apply_timeout` have no gate-phrase to surface. Kept
   * optional so older worker builds keep POSTing without it, and older
   * mobile clients keep parsing responses that don't include it.
   *
   * The mobile Inbox renders this verbatim beneath the alert title so
   * the user sees "Application form asked: Do you reside in San
   * Francisco?" instead of the misleading generic bucket. 500-char cap
   * matches the RunEvent `message` cap — the worker truncates upstream.
   */
  gateQuestion: z.string().min(1).max(500).optional(),
  status: AlertStatusReadable,
  occurrences: z.number().int().min(1),
  createdAt: IsoTimestamp,
  dismissedAt: IsoTimestamp.nullable(),
  /** Epoch-seconds DDB TTL. 30 days from createdAt. */
  ttl: z.number().int().positive(),
});
export type AlertItem = z.infer<typeof AlertItem>;

/**
 * Worker → API POST body. Server stamps `userId` from JWT and stamps
 * `status=open` + `occurrences` + `createdAt` + `ttl`. Client cannot
 * pre-set those fields (`.strict()` rejects unknowns). `kind` is
 * enum-validated at this boundary.
 */
export const CreateAlertRequest = z
  .object({
    alertKey: AlertKey,
    kind: AlertKind,
    runId: RunId,
    jobTitle: z.string().min(1).nullable(),
    company: z.string().min(1).nullable(),
    jobUrl: z.string().url().nullable(),
    atsHost: z.string().nullable(),
    message: z.string().min(1).max(2000),
    /**
     * #695 — location_mismatch alerts carry the raw ATS-form residency-
     * gate phrase (e.g. "Do you reside in San Francisco?") so the mobile
     * Inbox can surface it verbatim. Optional at this boundary because
     * the other three kinds do not fill it and pre-#695 workers do not
     * emit it.
     */
    gateQuestion: z.string().min(1).max(500).optional(),
  })
  .strict();
export type CreateAlertRequest = z.infer<typeof CreateAlertRequest>;

/**
 * Mobile → API PATCH body. Only `dismissed` is a client-driven
 * transition. `open` is worker-set initial state.
 */
export const PatchAlertRequest = z
  .object({
    status: z.literal('dismissed'),
  })
  .strict();
export type PatchAlertRequest = z.infer<typeof PatchAlertRequest>;

/**
 * `GET /v1/alerts` response.
 */
export const ListAlertsResponse = z.object({
  items: z.array(AlertItem),
});
export type ListAlertsResponse = z.infer<typeof ListAlertsResponse>;
